import React, { useEffect, useState } from "react";
import { Checkbox, TextField } from "@mui/material";

import { useLazyQuery } from "@apollo/client";
import { responseType } from "../../../../../utils/formTypes";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import {
  AtsAcLdgrEdge,
  AtsAcLdgrOrderField,
  GetSuppliersResult,
  GetSuppliersVars,
  LdgrQueryData,
  LdgrQueryType,
} from "../../masters/supplier/queries/types";
import { GetSuppliers } from "../../masters/supplier/queries/queries";
import {
  FETCH_MORE_DATA,
  MULTI_SELECT_LIMIT_TAGS,
  ROWS_PER_PAGE,
  debouncedDelay,
} from "../../../../../utils/constants";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import {
  FormAutocomplete,
  formClasses,
} from "../../../../../styles/AutocompleteStyles";
import LoadingModal from "../../../../pages/LoadingModal";
import { useDebounce } from "../../../../../customHooks/useDebounce";
interface Props {
  onChange: (newValue: responseType[]) => void;
  autoCompleteValue: responseType[];
  handleClear: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  input: LdgrQueryData;
}
const MultiSelectSupplier = ({
  onChange,
  handleClear,
  onKeyDown,
  autoCompleteValue,
  input,
}: Props) => {

  const { customer_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<AtsAcLdgrEdge[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<responseType[]>([]);

  const [GetSupplier, { data, loading, fetchMore }] = useLazyQuery<
    GetSuppliersResult,
    GetSuppliersVars
  >(GetSuppliers);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetSuppliers.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = suppliers.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setSuppliers(updatedNewData);
        setSupplierOptions(
          updatedNewData.map(({ node }) => ({
            label: node.desc,
            value: node.id,
          }))
        );
      } else {
        setSuppliers(newData);
        setSupplierOptions(
          newData.map(({ node }) => ({
            label: node.desc,
            value: node.id,
          }))
        );
      }
      setEndCursor(data.GetSuppliers.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    if (customer_id) {
      GetSupplier({
        variables: {
          after: null,
          customer_id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsAcLdgrOrderField.CREATED_AT,
          },
          
          name: debouncedSearch,
          input,
        },
      });
    }
  }, [customer_id, GetSupplier, debouncedSearch]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetSuppliers.edges;
            const pageInfo = fetchMoreResult.GetSuppliers.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetSuppliers.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetSuppliers: {
                edges: [...suppliers, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetSuppliers.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  return (
    <>
      <FormAutocomplete
        className={formClasses.inputRoot}
        options={supplierOptions}
        limitTags={MULTI_SELECT_LIMIT_TAGS}
        multiple
        onChange={(e, newValue) => {
          onChange(newValue as responseType[]);
        }}
        renderOption={(props, option) => {
          const selectedSuppliers = autoCompleteValue.map(({ value }) => value);

          const checked = selectedSuppliers.includes(
            (option as responseType).value
          );

          return (
            <li {...props} key={(option as responseType).value}>
              <Checkbox style={{ marginRight: 8 }} checked={checked} />
              {(option as responseType).label}
            </li>
          );
        }}
        slotProps={{
          listbox: {
            onScroll: handleScroll,
          },
        }}
        onKeyDown={onKeyDown}
        openOnFocus
        forcePopupIcon
        value={autoCompleteValue}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            onChange={(e) => {
              setSearchData(e.target.value);
            }}
            className={formClasses.formControlRoot}
          />
        )}
      />
      <LoadingModal flag={loading} />
    </>
  );
};

export default MultiSelectSupplier;
